import { Link } from "@tanstack/react-router";
import { Heart, Zap } from "lucide-react";
import LocaleSwitcher from "@/components/LocaleSwitcher";
import { m } from "@/paraglide/messages";

export default function SiteFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative border-t border-border bg-card/50 backdrop-blur-sm">
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />

      <div className="container mx-auto px-4 py-4 md:py-16">
        {/* Mobile: compact 2-col layout; Desktop: full 4-col grid */}
        <div className="hidden md:grid md:grid-cols-4 md:gap-10">
          {/* Brand Section — desktop */}
          <div className="md:col-span-2 flex flex-col gap-6">
            <Link
              to="/"
              className="group flex items-center gap-1.5 text-foreground hover:text-primary transition-colors w-fit"
            >
              <div className="flex items-center justify-center w-12 h-12">
                <img
                  src="/toolverse-icon.svg"
                  alt=""
                  className="w-11 h-11 object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-display text-lg font-bold tracking-wider">
                  TOOLVERSE
                </span>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              {m.footer_tagline()}
            </p>
          </div>

          {/* Tools — desktop: one link home instead of listing 49 tools */}
          <div className="flex flex-col gap-4">
            <h2 className="font-display text-sm font-semibold text-foreground flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" aria-hidden="true" />
              {m.footer_tools()}
            </h2>
            <nav className="flex flex-col gap-2">
              <Link
                to="/"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
              >
                {m.home_hero_cta_explore()}
              </Link>
            </nav>
          </div>

          {/* Company — desktop */}
          <div className="flex flex-col gap-4">
            <h2 className="font-display text-sm font-semibold text-foreground">
              {m.footer_company()}
            </h2>
            <nav className="flex flex-col gap-2">
              <Link
                to="/about"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
              >
                {m.footer_about()}
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
              >
                {m.footer_privacy()}
              </Link>
              <Link
                to="/terms"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
              >
                {m.footer_terms()}
              </Link>
              <Link
                to="/contact"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
              >
                {m.footer_contact()}
              </Link>
            </nav>
          </div>
        </div>

        {/* Mobile: compact layout */}
        <div className="md:hidden space-y-4">
          {/* Brand row */}
          <div className="flex items-center gap-2">
            <img
              src="/toolverse-icon.svg"
              alt=""
              className="w-8 h-8 object-contain"
            />
            <Link
              to="/"
              className="font-display text-base font-bold tracking-wider text-foreground hover:text-primary transition-colors"
            >
              TOOLVERSE
            </Link>
          </div>

          {/* Tools + Company side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Zap className="h-3 w-3 text-primary" aria-hidden="true" />
                {m.footer_tools()}
              </h2>
              <nav className="flex flex-col gap-1.5">
                <Link
                  to="/"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
                >
                  {m.home_hero_cta_explore()}
                </Link>
              </nav>
            </div>

            <div className="flex flex-col gap-2">
              <h2 className="font-display text-xs font-semibold text-foreground">
                {m.footer_company()}
              </h2>
              <nav className="flex flex-col gap-1.5">
                <Link
                  to="/about"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
                >
                  {m.footer_about()}
                </Link>
                <Link
                  to="/privacy"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
                >
                  {m.footer_privacy()}
                </Link>
                <Link
                  to="/terms"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
                >
                  {m.footer_terms()}
                </Link>
                <Link
                  to="/contact"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
                >
                  {m.footer_contact()}
                </Link>
              </nav>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-3 pt-3 md:mt-12 md:pt-8 border-t border-border/50 flex flex-col gap-2 md:gap-4 md:flex-row md:items-center md:justify-between">
          {/* suppressHydrationWarning: getFullYear() can differ between the
              server (UTC) and client (local tz) across a New Year boundary,
              which would otherwise throw a hydration mismatch (React #418). */}
          <p
            className="text-xs md:text-sm text-muted-foreground font-mono"
            suppressHydrationWarning
          >
            © {currentYear} TOOLVERSE
          </p>
          <div className="flex items-center gap-3 md:gap-4">
            <p className="flex items-center gap-1.5 text-xs md:text-sm text-muted-foreground">
              <span>{m.footer_made_with()}</span>
              <Heart className="h-3 w-3 md:h-4 md:w-4 text-destructive animate-pulse" />
            </p>
            <LocaleSwitcher />
          </div>
        </div>
      </div>

      {/* Bottom gradient accent */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-30" />
    </footer>
  );
}
